
import Button from '../../../components/base/Button';

export default function ActiveCampaignsSection() {
  const campaigns = [
    {
      title: "National Pharmacare Implementation",
      description: "Advocating for the inclusion of pharmacy and veterinary services in Canada's national pharmacare program.",
      urgency: "High",
      deadline: "June 2025",
      supporters: 12500,
      target: 15000,
      actions: ["Sign Petition", "Contact MP", "Share Campaign"],
      icon: "ri-heart-add-line",
      image: "https://public.readdy.ai/ai/img_res/2011737051fc5f35ec072e2bd151b449.jpg"
    },
    {
      title: "Rural Healthcare Access Initiative",
      description: "Expanding telehealth and mobile pharmacy services to underserved rural and remote Canadian communities.",
      urgency: "Medium",
      deadline: "September 2025",
      supporters: 8750,
      target: 10000,
      actions: ["Join Coalition", "Volunteer", "Donate"],
      icon: "ri-road-map-line",
      image: "https://readdy.ai/api/search-image?query=Rural%20Canadian%20healthcare%20outreach%20showing%20mobile%20pharmacy%20and%20veterinary%20services%20van%20in%20remote%20community%2C%20healthcare%20professionals%20serving%20rural%20families%20with%20mountains%20and%20farmland%20in%20background%2C%20emphasizing%20healthcare%20accessibility&width=600&height=300&seq=rural-healthcare-001&orientation=landscape"
    },
    {
      title: "Mental Health Support for Healthcare Workers",
      description: "Securing funding and resources for mental health support programs specifically for pharmacy and veterinary professionals.",
      urgency: "High",
      deadline: "April 2025",
      supporters: 15200,
      target: 20000,
      actions: ["Share Story", "Fundraise", "Advocate"],
      icon: "ri-mental-health-line",
      image: "https://readdy.ai/api/search-image?query=Healthcare%20workers%20mental%20health%20support%20group%20meeting%20with%20pharmacists%20and%20veterinarians%20in%20comfortable%20counseling%20setting%2C%20showing%20emotional%20support%20and%20professional%20wellness%2C%20warm%20therapeutic%20environment&width=600&height=300&seq=mental-health-support-001&orientation=landscape"
    }
  ];

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'High':
        return 'bg-red-100 text-red-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'Low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-megaphone-line text-2xl text-orange-600"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Active Campaigns
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Join our current advocacy efforts and help shape the future of healthcare in Canada
          </p>
        </div>

        <div className="space-y-8">
          {campaigns.map((campaign, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="md:flex">
                <div className="md:w-1/3">
                  <img 
                    src={campaign.image} 
                    alt={campaign.title}
                    className="w-full h-64 md:h-full object-cover object-top"
                  />
                </div>
                <div className="md:w-2/3 p-8">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                        <i className={`${campaign.icon} text-xl text-blue-600`}></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {campaign.title}
                      </h3>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getUrgencyColor(campaign.urgency)}`}>
                        {campaign.urgency} Priority
                      </span>
                      <span className="text-sm text-gray-500">Deadline: {campaign.deadline}</span>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-6">
                    {campaign.description}
                  </p>

                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Campaign Progress</span>
                      <span className="text-sm text-gray-500">
                        {campaign.supporters.toLocaleString()} / {campaign.target.toLocaleString()} supporters
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${(campaign.supporters / campaign.target) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {campaign.actions.map((action, actionIndex) => (
                      <Button 
                        key={actionIndex}
                        className={`${actionIndex === 0 ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} px-4 py-2 whitespace-nowrap`}
                      >
                        {action}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Can't find a campaign that matches your interest?
          </h3>
          <p className="text-gray-600 mb-6">
            Suggest a new advocacy initiative or join our general advocacy network to stay informed about all our efforts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-blue-600 text-white hover:bg-blue-700 px-6 py-3 whitespace-nowrap">
              Suggest New Campaign
            </Button>
            <Button className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-6 py-3 whitespace-nowrap">
              Join Advocacy Network
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
